import React from 'react';
import { Formik } from 'formik';
import { Col, DatePicker, Row, Tooltip } from 'antd';
import { Form, FormItem, Input, ResetButton, SubmitButton, Radio, Select } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { InfoCircleOutlined } from '@ant-design/icons';
import Yup from '../../../../vendor/yup';
import useDatepickerHandler from '../../../../app/hooks/useDatepickerHandler';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { POPUP_TEMPLATE_CONTENT_TYPE } from '../FormCreatePopup/FormCreatePopup.const';
import { DATE_FORMAT } from '../../../../const/system';
import { Permissions } from '../../../../const/permissions';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import mkTemplatesOptions from '../../../Content/utils/mkTemplatesOptions';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import getNotEmptyObjectValues from '../../../../lib/getNotEmptyObjectValues';
import { useGetTemplatesQuery } from '../../../Template builder/api/templatesApiSlice';



const validationSchema = Yup.lazy((values) => {
  return Yup.object().shape({
    id: Yup.string(),
    title: Yup.string().max(255, 'Maximum 255 symbols required'),
    template: Yup.string(),
    released_at: Yup.string().test(
      'test_date_after',
      'Released at date must be same or before than released to',
      (value) => {
        if (!isEmpty(values?.released_to) && !isEmpty(value)) {
          return moment(values?.released_to).isSameOrAfter(value);
        }
        return true;
      },
    ),
    released_to: Yup.string().test(
      'test_date_before',
      'Released to date must be same or after than released at',
      (value) => {
        if (!isEmpty(values?.released_at) && !isEmpty(value)) {
          return moment(values?.released_at).isSameOrBefore(value);
        }
        return true;
      },
    ),
  });
});


const FormPopupsFilter = ({
  initialValues = {},
  formErrors = {},
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const intl = useIntl();
  const { onBlur } = useDatepickerHandler();

  const { data: { data: templates } = { data: [] }, isFetching: isFetchingTemplates } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('is_used', POPUP_TEMPLATE_CONTENT_TYPE),
  });

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        onSubmit(getNotEmptyObjectValues(values));
      }}
      onReset={onCancel}
    >
      {({ isValid, dirty, values, setFieldValue, setFieldTouched }) => {
        return (
          <Form layout="vertical" >
            <FormItem
              name='id'
              label={
                <Row className='w-full' justify="space-between" align="middle">
                  <IntlMessages id="ui-general-id" />
                  <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                    <InfoCircleOutlined className="hp-ml-8" />
                  </Tooltip>
                </Row>
              }
            >
              <Input
                name="id"
                placeholder={intl.formatMessage({ id: 'ui-general-ids-placeholder' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'ui-general-title' })}
              name='title'
            >
              <Input
                name="title"
                placeholder={intl.formatMessage({ id: 'ui-general-specify-title' })}
              />
            </FormItem>

            <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.FILTER ]}>
              <FormItem
                label={intl.formatMessage({ id: 'ui-general-template' })}
                name='template'
              >
                <Select
                  allowClear
                  showSearch
                  loading={isFetchingTemplates}
                  name="template"
                  value={values?.template?.toString() ?? null}
                  options={mkTemplatesOptions(templates)}
                  placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                  filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                />
              </FormItem>
            </PrivateComponent>

            <Row gutter={[ 16, 16 ]}>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-released-at' })}
                  name='released_at'
                >
                  <DatePicker
                    name='released_at'
                    format={DATE_FORMAT}
                    showToday
                    value={values?.released_at ? moment(values.released_at) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('released_at', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('released_at', true);
                      await onBlur('released_at', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-released-to' })}
                  name='released_to'
                >
                  <DatePicker
                    name='released_to'
                    format={DATE_FORMAT}
                    value={values?.released_to ? moment(values.released_to) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('released_to', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('released_to', true);
                      await onBlur('released_to', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-published' })}
                  name='is_published'
                >
                  <Radio.Group
                    size='small'
                    name='is_published'
                    defaultValue={null}
                  >
                    <Radio.Button value={null}>{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                    <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                    <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                  </Radio.Group>
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <ResetButton disabled={false}>
                  <IntlMessages id='ui-general-reset' />
                </ResetButton>
              </Col>

              <Col>
                <SubmitButton
                  type="primary"
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  <IntlMessages id="ui-general-apply" />
                </SubmitButton>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormPopupsFilter.propTypes = {
  initialValues: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormPopupsFilter;
